import { useState } from "react";
import { Instagram, Mail, MapPin, MessageCircle, Phone, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiErrorDetail } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { useTexts } from "@/lib/texts";
import { CONTACTS, STORES, waLink } from "@/lib/constants";

const initialForm = { name: "", email: "", phone: "", message: "" };

export default function Contatti() {
  const texts = useTexts();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [mapStore, setMapStore] = useState(0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await client.post("/messages", form);
      toast.success("Messaggio inviato. Ti risponderemo al più presto.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-5 pb-28 pt-32 md:px-8 md:pt-40" data-testid="contatti-page">
      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Contatti</p>
      </Reveal>
      <MaskedLine className="mt-4 font-serif text-5xl md:text-7xl" delay={0.1}>{texts.contatti_title}</MaskedLine>
      <Reveal delay={0.25}>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg">
          {texts.contatti_sub}
        </p>
      </Reveal>

      <div className="mt-16 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Nome *</label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="mt-2 w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-[#FAFAFA] transition-colors"
                  placeholder="Il tuo nome"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Telefono</label>
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  className="mt-2 w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-[#FAFAFA] transition-colors"
                  placeholder="+39 …"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-email" className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Email *</label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                className="mt-2 w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-[#FAFAFA] transition-colors"
                placeholder="nome@email.it"
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Messaggio *</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                className="mt-2 w-full resize-none border border-white/15 bg-transparent px-4 py-3 text-sm text-[#FAFAFA] transition-colors"
                placeholder="Come possiamo aiutarti?"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-50"
            >
              <Send size={14} />
              {sending ? "Invio in corso…" : "Invia messaggio"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-6">
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2" data-testid="contact-channels">
            <a
              href={waLink("Buongiorno, vorrei informazioni.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="group flex flex-col bg-[#050505] p-7 transition-colors hover:bg-[#0d0d0d]"
            >
              <MessageCircle size={20} className="text-gold" />
              <p className="mt-4 font-serif text-xl">WhatsApp</p>
              <p className="mt-2 text-sm text-[#A3A3A3]">Risposta rapida, anche con foto dei tuoi oggetti.</p>
              <span className="mt-4 text-[10px] uppercase tracking-[0.25em] text-gold">Scrivici ora</span>
            </a>
            <a
              href={CONTACTS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-card"
              className="group flex flex-col bg-[#050505] p-7 transition-colors hover:bg-[#0d0d0d]"
            >
              <Instagram size={20} className="text-gold" />
              <p className="mt-4 font-serif text-xl">Instagram</p>
              <p className="mt-2 text-sm text-[#A3A3A3]">{CONTACTS.instagramHandle} · nuovi arrivi ogni settimana.</p>
              <span className="mt-4 text-[10px] uppercase tracking-[0.25em] text-gold">Seguici</span>
            </a>
            <a
              href={`mailto:${CONTACTS.email}`}
              data-testid="contact-email-card"
              className="group flex flex-col bg-[#050505] p-7 transition-colors hover:bg-[#0d0d0d]"
            >
              <Mail size={20} className="text-gold" />
              <p className="mt-4 font-serif text-xl">Email</p>
              <p className="mt-2 text-sm text-[#A3A3A3]">{CONTACTS.email}</p>
              <span className="mt-4 text-[10px] uppercase tracking-[0.25em] text-gold">Scrivici</span>
            </a>
            <a
              href={`tel:${STORES[0].tel}`}
              data-testid="contact-phone-card"
              className="group flex flex-col bg-[#050505] p-7 transition-colors hover:bg-[#0d0d0d]"
            >
              <Phone size={20} className="text-gold" />
              <p className="mt-4 font-serif text-xl">Telefono</p>
              <p className="mt-2 text-sm text-[#A3A3A3]">Sede di Via Porpora: {STORES[0].phone}</p>
              <span className="mt-4 text-[10px] uppercase tracking-[0.25em] text-gold">Chiama ora</span>
            </a>
          </div>

          <div className="mt-10 space-y-6" data-testid="contact-stores">
            {STORES.map((s) => (
              <Reveal key={s.id}>
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 pb-5" data-testid={`contact-store-${s.id}`}>
                  <div>
                    <p className="font-serif text-xl">{s.name}</p>
                    <p className="mt-1 flex items-center gap-2 text-sm text-[#A3A3A3]">
                      <MapPin size={13} className="text-gold/70" /> {s.address}, {s.zip}
                    </p>
                    {s.hours && (
                      <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-[#737373]" data-testid={`contact-store-${s.id}-hours`}>
                        <Clock size={12} className="mt-0.5 shrink-0 text-gold/70" />
                        <span>
                          {s.hours.map((h) => (
                            <span key={h} className="block">{h}</span>
                          ))}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-5 text-[10px] uppercase tracking-[0.25em]">
                    <a href={`tel:${s.tel}`} data-testid={`contact-store-${s.id}-call`} className="text-gold transition-colors hover:text-gold-pale">
                      {s.phone}
                    </a>
                    <a href={s.map} target="_blank" rel="noopener noreferrer" data-testid={`contact-store-${s.id}-map`} className="text-[#A3A3A3] transition-colors hover:text-gold">
                      Mappa
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <section className="mt-24" data-testid="stores-map-section">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Sulla mappa</p>
        </Reveal>
        <MaskedLine className="mt-4 font-serif text-3xl md:text-5xl" delay={0.1}>Le nostre tre sedi</MaskedLine>
        <div className="mt-8 flex flex-wrap gap-3">
          {STORES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`map-tab-${s.id}`}
              onClick={() => setMapStore(i)}
              className={`rounded-full border px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
                mapStore === i ? "border-gold bg-gold text-black" : "border-white/15 text-[#A3A3A3] hover:border-gold/60 hover:text-gold"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-6 overflow-hidden border border-white/10">
            <iframe
              key={STORES[mapStore].id}
              src={`https://maps.google.com/maps?q=${encodeURIComponent(`${STORES[mapStore].address}, ${STORES[mapStore].zip}`)}&z=16&output=embed`}
              title={`Mappa ${STORES[mapStore].name}`}
              loading="lazy"
              data-testid="stores-map-iframe"
              className="h-[420px] w-full grayscale invert-[0.92] contrast-[0.85]"
            />
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#737373]">
            {STORES[mapStore].address}, {STORES[mapStore].zip} ·{" "}
            <a href={STORES[mapStore].map} target="_blank" rel="noopener noreferrer" data-testid="map-directions-link" className="text-gold transition-colors hover:text-gold-pale">
              Apri in Google Maps
            </a>
          </p>
        </Reveal>
      </section>
    </main>
  );
}
